import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { agentCategoryService } from "../../../../services/agentCategoryService";
import { agentService } from "../../../../services/agentService";
import { industryService } from "../../../../services/industryService";
import { mcpService, type McpMasterServer } from "../../../../services/mcpService";
import {
  PricingPlansEditor,
  draftFromApiPlan,
  serializePlanDraft,
  type PricingPlanDraft,
} from "../../../../components/pricing/PricingPlansEditor";
import { Button } from '../../../../components/ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../../../../components/ui/Field';
import { Overlay } from '../../../../components/ui/Overlay';

/* ===================== TYPES ===================== */

export type IntentExample = {
  id: string;
  text: string;
};

export type CapabilityIntent = {
  id: string;
  capabilityCode: string;
  description?: string;
  examples: IntentExample[];
};

export type AgentTypeDTO = {
  id: string;
  code: string;
  name: string;
  shortDescription?: string;
  description?: string;
  industryId?: string;
  categoryId?: string;
  industry?: { id: string; name: string } | null;
  category?: { id: string; name: string } | null;
  isActive: boolean;
  metadata: string;
  version: number;
  capabilities: CapabilityIntent[];
  enabledMcpServerIds?: string[];
  mcpIntegrations?: Array<{ serverId?: string; server?: { id: string; name: string; code: string } }>;
};

type Props = {
  isOpen: boolean;
  isEditing: boolean;
  agentTypeDetails?: AgentTypeDTO;
  onClose: () => void;
  refreshList: () => void;
};

/* ===================== COMPONENT ===================== */

const AgentTypeModal: React.FC<Props> = ({
  isOpen,
  isEditing,
  agentTypeDetails,
  onClose,
  refreshList,
}) => {
  const [agentType, setAgentType] = useState<AgentTypeDTO>({
    id: "",
    code: "",
    name: "",
    shortDescription: "",
    description: "",
    industryId: "",
    categoryId: "",
    isActive: true,
    metadata: "{}",
    version: 1,
    capabilities: [],
    enabledMcpServerIds: [],
  });
  const [industryOptions, setIndustryOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [categoryOptions, setCategoryOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [step, setStep] = useState<1 | 2>(1);
  const [generatingIntents, setGeneratingIntents] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pricingFree, setPricingFree] = useState(true);
  const [pricingPlans, setPricingPlans] = useState<PricingPlanDraft[]>([]);
  const [mcpMasters, setMcpMasters] = useState<McpMasterServer[]>([]);

  const applyPricingFromMetadataString = (metadataStr: string) => {
    try {
      const obj = JSON.parse(metadataStr?.trim() || "{}");
      const p = obj?.pricing;
      const isFree = !p || p.isFree !== false;
      setPricingFree(isFree);
      const plans = Array.isArray(p?.plans)
        ? p.plans.map((pl: Record<string, unknown>) => draftFromApiPlan(pl))
        : [];
      setPricingPlans(plans);
    } catch {
      /* invalid JSON — leave pricing UI unchanged */
    }
  };

  /* ---------- Edit mode ---------- */
  useEffect(() => {
    if (!agentTypeDetails) return;

    const metadataStr =
      typeof agentTypeDetails.metadata === "string"
        ? agentTypeDetails.metadata
        : JSON.stringify(agentTypeDetails.metadata, null, 2);

    setAgentType({
      ...agentTypeDetails,
      industryId: agentTypeDetails.industryId || agentTypeDetails.industry?.id || "",
      categoryId: agentTypeDetails.categoryId || agentTypeDetails.category?.id || "",
      metadata: metadataStr,

      capabilities: agentTypeDetails.capabilities.map((cap) => ({
        id: cap.id ?? uuidv4(),
        capabilityCode: cap.capabilityCode,
        description: cap.description ?? "",
        examples: (cap.examples ?? []).map((ex: any) => ({
          id: uuidv4(),
          text: typeof ex === "string" ? ex : ex.text,
        })),
      })),
      enabledMcpServerIds:
        agentTypeDetails.enabledMcpServerIds ||
        (agentTypeDetails.mcpIntegrations || [])
          .map((integration) => integration.serverId || integration.server?.id)
          .filter(Boolean) as string[],
    });
    applyPricingFromMetadataString(metadataStr);
  }, [agentTypeDetails]);

  /* ---------- Create modal: reset pricing when no details ---------- */
  useEffect(() => {
    if (!isOpen || agentTypeDetails) return;
    setPricingFree(true);
    setPricingPlans([]);
  }, [isOpen, agentTypeDetails]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      try {
        const [industriesRes, categoriesRes, mcpRes] = await Promise.all([
          industryService.list({ limit: 100, isActive: true }),
          agentCategoryService.list({ limit: 100, isActive: true }),
          mcpService.listMasterServers({ limit: 100, isActive: true }),
        ]);

        const industriesRaw: any =
          (industriesRes as any)?.industries || (industriesRes as any)?.data?.industries || [];
        const categoriesRaw: any =
          (categoriesRes as any)?.agentCategories || (categoriesRes as any)?.data?.agentCategories || [];
        const mcpItems: any =
          (mcpRes as any)?.items || (mcpRes as any)?.data?.items || [];

        if (!cancelled) {
          setIndustryOptions(
            Array.isArray(industriesRaw)
              ? industriesRaw.map((i: any) => ({ id: String(i.id), name: String(i.name) }))
              : []
          );
          setCategoryOptions(
            Array.isArray(categoriesRaw)
              ? categoriesRaw.map((c: any) => ({ id: String(c.id), name: String(c.name) }))
              : []
          );
          setMcpMasters(Array.isArray(mcpItems) ? mcpItems : []);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load industry/category options");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  /* ---------- Handlers ---------- */
  const updateField = (name: keyof AgentTypeDTO, value: any) => {
    setError(null);
    setAgentType((prev) => ({ ...prev, [name]: value }));
  };

  const mergePricingIntoMetadata = (isFree: boolean, plans: PricingPlanDraft[]) => {
    try {
      JSON.parse(agentType.metadata?.trim() || "{}");
    } catch {
      setError("Fix metadata JSON before editing pricing.");
      return;
    }
    setError(null);
    setAgentType((prev) => {
      let obj: Record<string, unknown>;
      try {
        obj = JSON.parse(prev.metadata?.trim() || "{}");
      } catch {
        return prev;
      }
      if (isFree) {
        delete obj.pricing;
      } else {
        const serialized = plans
          .map(serializePlanDraft)
          .filter((x): x is Record<string, unknown> => x !== null);
        obj.pricing = { isFree: false, plans: serialized };
      }
      return { ...prev, metadata: JSON.stringify(obj, null, 2) };
    });
  };

  const handleMetadataChange = (v: string) => {
    updateField("metadata", v);
    applyPricingFromMetadataString(v);
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
  }, [isOpen, agentTypeDetails?.id]);

  const validateStepOne = () => {
    if (!agentType.name.trim()) {
      setError("Agent name is required");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStepOne()) return;
    setStep(2);
  };

  /* ---------- Save ---------- */
  const handleSave = async () => {
    if (!validateStepOne()) return;

    for (const cap of agentType.capabilities) {
      if (!cap.capabilityCode.trim()) {
        return setError("Each intent must have a code");
      }
      if (!cap.examples.length) {
        return setError(
          `Intent "${cap.capabilityCode}" must have at least one example`
        );
      }
    }

    let metadataObj: Record<string, unknown>;
    try {
      metadataObj = agentType.metadata?.trim()
        ? JSON.parse(agentType.metadata)
        : {};
    } catch {
      return setError("Metadata must be valid JSON");
    }

    if (pricingFree) {
      delete metadataObj.pricing;
    } else {
      if (!pricingPlans.length) {
        return setError("Add at least one pricing plan, or keep “Free agent type” checked.");
      }
      const serialized = pricingPlans.map(serializePlanDraft);
      if (serialized.some((p) => p === null)) {
        return setError(
          "Each paid plan needs a plan key, display name, a 3-letter currency (e.g. USD), and a positive price."
        );
      }
      for (let i = 0; i < pricingPlans.length; i++) {
        const p = pricingPlans[i];
        const ic = parseInt(p.intervalCount, 10);
        if (!Number.isFinite(ic) || ic < 1) {
          return setError(
            `Plan “${p.planKey.trim() || String(i + 1)}”: enter a valid billing interval count (≥ 1).`
          );
        }
      }
      metadataObj.pricing = { isFree: false, plans: serialized as unknown[] };
    }

    const payload = {
      name: agentType.name,
      shortDescription: agentType.shortDescription,
      description: agentType.description,
      industryId: agentType.industryId || undefined,
      categoryId: agentType.categoryId || undefined,
      isActive: agentType.isActive,
      version:
        Number.isInteger(agentType.version) && Number(agentType.version) > 0
          ? Number(agentType.version)
          : undefined,
      metadata: metadataObj,
      capabilities: agentType.capabilities.map((c) => ({
        capabilityCode: c.capabilityCode,
        description: c.description,
        examples: c.examples.map((e) => e.text),
      })),
      enabledMcpServerIds: agentType.enabledMcpServerIds || [],
    };

    try {
      setLoading(true);
      console.log("Payload to save:", payload);
      isEditing
        ? await agentService.updateAgentType(agentType.id, payload)
        : await agentService.createAgentType(payload);

      toast.success(isEditing ? "Agent Type Updated" : "Agent Type Created");
      await refreshList();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save Agent Type");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateIntentsWithAI = async () => {
    if (!agentType.name.trim()) {
      setError("Agent name is required");
      setStep(1);
      return;
    }

    const description = (agentType.description || "").trim();
    if (!description) {
      toast.error("Please add agent description in step 1 before generating intents");
      return;
    }

    const industryName =
      industryOptions.find((item) => item.id === agentType.industryId)?.name || "";
    if (!industryName) {
      toast.error("Please select industry in step 1 before generating intents");
      return;
    }

    if (
      agentType.capabilities.length > 0 &&
      !window.confirm("This will replace existing intents. Continue?")
    ) {
      return;
    }

    try {
      setGeneratingIntents(true);
      const generated = await agentService.generateAgentTypeIntents({
        name: agentType.name,
        description,
        industry: industryName,
      });

      const generatedCapabilities = (generated?.capabilities || []).map((cap) => ({
        id: uuidv4(),
        capabilityCode: cap.capabilityCode || "",
        description: cap.description || "",
        examples: (cap.examples || []).map((example: string) => ({
          id: uuidv4(),
          text: example,
        })),
      }));

      if (!generatedCapabilities.length) {
        toast.error("No intents were generated");
        return;
      }

      updateField("capabilities", generatedCapabilities);
      toast.success("Intents generated successfully");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to generate intents using AI");
    } finally {
      setGeneratingIntents(false);
    }
  };

  if (!isOpen) return null;

  /* ===================== UI ===================== */

  return (
    <Overlay className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!loading && window.confirm("Discard changes?")) onClose();
        }}
      />

      <div className="relative w-[70vw] max-h-[85vh] overflow-y-auto bg-white rounded-xl shadow-xl p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold">
            {isEditing ? "Edit Agent Type" : "Create Agent Type"}
          </h2>
          <p className="text-sm text-gray-500">
            Configure how this agent understands user intent
          </p>
          <p className="text-xs text-gray-500 mt-1">Step {step} of 2</p>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border p-2 rounded">
            {error}
          </div>
        )}

        {step === 1 ? (
          <>
            {/* Basic Info */}
            <section className="grid grid-cols-1 gap-4">
              <Input
                label="Agent Name"
                value={agentType.name}
                onChange={(v) => updateField("name", v)}
              />
              <Input
                label="Short Description"
                value={agentType.shortDescription ?? ""}
                onChange={(v) => updateField("shortDescription", v)}
              />
            </section>

            <section className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Industry</label>
                <SelectField
                  value={agentType.industryId || ""}
                  onChange={(e) => updateField("industryId", e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">-- Select industry --</option>
                  {industryOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                <SelectField
                  value={agentType.categoryId || ""}
                  onChange={(e) => updateField("categoryId", e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">-- Select category --</option>
                  {categoryOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </SelectField>
              </div>
            </section>

            <Textarea
              label="Description"
              value={agentType.description ?? ""}
              onChange={(v) => updateField("description", v)}
            />

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
               
                checked={agentType.isActive}
                onChange={(e) => updateField("isActive", e.target.checked)}
              />
              Active
            </label>

            <section className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Enabled MCP Integrations</p>
              <p className="text-xs text-gray-500">
                These master integrations will be available for agents created from this type.
              </p>
              {mcpMasters.length ? (
                <div className="grid grid-cols-1 gap-2 rounded border border-gray-200 p-3 md:grid-cols-2">
                  {mcpMasters.map((server) => {
                    const checked = (agentType.enabledMcpServerIds || []).includes(server.id);
                    return (
                      <label key={server.id} className="flex items-center gap-2 text-sm text-gray-800">
                        <Checkbox
                         
                          checked={checked}
                          onChange={(e) => {
                            const current = new Set(agentType.enabledMcpServerIds || []);
                            if (e.target.checked) current.add(server.id);
                            else current.delete(server.id);
                            updateField("enabledMcpServerIds", Array.from(current));
                          }}
                        />
                        <span>{server.name}</span>
                        <span className="text-xs text-gray-500">({server.code})</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-amber-600">
                  No active MCP masters found. Create MCP masters in the registry first.
                </p>
              )}
            </section>
          </>
        ) : (
          <>
            {/* Capabilities */}
            <CapabilitiesEditor
              capabilities={agentType.capabilities}
              setCapabilities={(caps) => updateField("capabilities", caps)}
              onGenerateUsingAI={handleGenerateIntentsWithAI}
              isGeneratingAI={generatingIntents}
            />

            {/* Metadata */}
            <Textarea
              label="Agent Metadata (JSON)"
              mono
              value={agentType.metadata}
              onChange={handleMetadataChange}
            />

            <PricingPlansEditor
              pricingFree={pricingFree}
              pricingPlans={pricingPlans}
              itemLabel="agent type"
              metadataHint="metadata.pricing (see Agent Metadata above)"
              onFreeChange={(nextFree) => {
                setPricingFree(nextFree);
                mergePricingIntoMetadata(nextFree, pricingPlans);
              }}
              onPlansChange={(nextPlans) => {
                setPricingPlans(nextPlans);
                if (!pricingFree) mergePricingIntoMetadata(false, nextPlans);
              }}
            />
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <Button tone="custom"
            className="border px-4 py-2 rounded"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          {step === 2 && (
            <Button tone="custom"
              className="border px-4 py-2 rounded"
              onClick={() => setStep(1)}
              disabled={loading}
            >
              Back
            </Button>
          )}
          {step === 1 ? (
            <Button tone="blue-static"
              className="px-4 py-2 rounded"
              onClick={handleNext}
              disabled={loading}
            >
              Next
            </Button>
          ) : (
            <Button tone="blue-static"
              className="px-4 py-2 rounded disabled:opacity-50"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : isEditing ? "Save Changes" : "Create Agent"}
            </Button>
          )}
        </div>
      </div>
    </Overlay>
  );
};

/* ===================== CAPABILITIES ===================== */

const CapabilitiesEditor = ({
  capabilities,
  setCapabilities,
  onGenerateUsingAI,
  isGeneratingAI,
}: {
  capabilities: CapabilityIntent[];
  setCapabilities: (c: CapabilityIntent[]) => void;
  onGenerateUsingAI: () => void;
  isGeneratingAI: boolean;
}) => {
  const update = (id: string, patch: Partial<CapabilityIntent>) =>
    setCapabilities(
      capabilities.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );

  const removeIntent = (id: string) => {
    if (!window.confirm("Remove this intent?")) return;
    setCapabilities(capabilities.filter((c) => c.id !== id));
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-lg">Intents (Capabilities)</h3>
        <Button tone="custom"
          type="button"
          className="border px-3 py-2 rounded text-sm"
          onClick={onGenerateUsingAI}
          disabled={isGeneratingAI}
        >
          {isGeneratingAI ? "Generating..." : "Generate Using AI"}
        </Button>
      </div>
      <p className="text-sm text-gray-500">
        Each intent represents a possible user goal. Examples are embedded and
        stored in the vector database.
      </p>

      {capabilities.map((cap, idx) => (
        <div key={cap.id} className="border rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">Intent {idx + 1}</h4>
            <Button tone="danger-text"
              className="text-sm"
              onClick={() => removeIntent(cap.id)}
            >
              Remove Intent
            </Button>
          </div>

          <Input
            label="Intent Code"
            value={cap.capabilityCode}
            onChange={(v) => update(cap.id, { capabilityCode: v })}
          />

          <Textarea
            label="Intent Description"
            value={cap.description ?? ""}
            onChange={(v) => update(cap.id, { description: v })}
          />

          <ExamplesEditor
            examples={cap.examples}
            setExamples={(ex) => update(cap.id, { examples: ex })}
          />
        </div>
      ))}

      <Button tone="custom"
        onClick={() =>
          setCapabilities([
            ...capabilities,
            {
              id: uuidv4(),
              capabilityCode: "",
              description: "",
              examples: [{ id: uuidv4(), text: "" }],
            },
          ])
        }
        className="border border-dashed px-4 py-2 rounded"
      >
        + Add Intent
      </Button>
    </section>
  );
};

/* ===================== EXAMPLES ===================== */

const ExamplesEditor = ({
  examples,
  setExamples,
}: {
  examples: IntentExample[];
  setExamples: (e: IntentExample[]) => void;
}) => (
  <div>
    <p className="text-sm font-medium">Training Examples</p>
    <p className="text-xs text-gray-500 mb-2">
      These sentences are embedded and used for intent matching
    </p>

    {examples.map((ex) => (
      <div key={ex.id} className="flex gap-2 mb-2">
        <TextField
          className="flex-1 border px-2 py-1 rounded"
          value={ex.text}
          onChange={(e) =>
            setExamples(
              examples.map((x) =>
                x.id === ex.id ? { ...x, text: e.target.value } : x
              )
            )
          }
        />
        <Button tone="custom"
          onClick={() => setExamples(examples.filter((x) => x.id !== ex.id))}
        >
          ✕
        </Button>
      </div>
    ))}

    <Button tone="custom"
      className="text-sm text-blue-600"
      onClick={() =>
        setExamples([...examples, { id: uuidv4(), text: "" }])
      }
    >
      + Add example
    </Button>
  </div>
);

/* ===================== PRIMITIVES ===================== */

const Input = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <TextField
      className="w-full border px-3 py-2 rounded"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const Textarea = ({
  label,
  value,
  onChange,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <TextArea
      className={`w-full border px-3 py-2 rounded ${mono ? "font-mono text-sm" : ""
        }`}
      rows={4}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default AgentTypeModal;
